import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { 
  User as UserIcon, 
  Mail, 
  Phone, 
  MapPin, 
  Building2, 
  Shield, 
  Bell, 
  Settings,
  Camera,
  Save,
  Eye,
  EyeOff,
  Clock,
  Monitor,
  Smartphone,
  Activity,
  Key,
  Loader2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { userApiService } from '../services';
import type { User, UserRole, UserBranch, ConsolidatedUserProfile, UserPreferences } from '../services';
import { useAuth } from '../contexts/AuthContext';

interface LocalUserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  branch: string;
  employeeId: string;
  joinDate: string;
  profileImage?: string;
  bio: string;
  preferences: {
    defaultBranch: string;
    emailNotifications: boolean;
    pushNotifications: boolean;
    darkMode: boolean;
    language: string;
    timezone: string;
  };
}

interface ActivityLog {
  id: string;
  action: string;
  timestamp: string;
  device: string;
  location: string;
}

// Mock data removed - now using actual API data

// Static data arrays removed - now using dynamic data from API

export function UserProfilePage() {
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState<LocalUserProfile | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [consolidatedProfile, setConsolidatedProfile] = useState<ConsolidatedUserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'preferences' | 'activity'>('profile');
  // const [activeTab, setActiveTab] = useState<'profile' | 'security'>('profile');

  const handleProfileUpdate = (field: keyof LocalUserProfile, value: any) => {
    setProfile(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handlePreferenceUpdate = (field: keyof LocalUserProfile['preferences'], value: any) => {
    setProfile(prev => {
      if (!prev) return null;
      return {
        ...prev,
        preferences: {
          ...prev.preferences,
          [field]: value
        }
      };
    });
  };

  // Load current user profile
  useEffect(() => {
    const loadCurrentUserProfile = async () => {
      // Check if we have the current user from context
      if (!currentUser?.id) {
        // console.error('No current user ID available from authentication context');
        toast.error('User authentication required. Please login again.');
        return;
      }

      try {
        setLoading(true);
        
        // Use the current user's ID from context instead of hardcoded value
        const response = await userApiService.getConsolidatedUserProfile(currentUser.id);
        // const response = await userApiService.getConsolidatedUserProfile(9);

        
        if (response.success && response.data) {
          const consolidatedData = response.data;
          
          // Debug: Log what we received
          // console.log('🔍 API Response Data:', consolidatedData);
          // console.log('🔍 Preferences object:', consolidatedData.preferences);
          // console.log('🔍 Recent activities:', consolidatedData.recentActivities);
          // console.log('🔍 Roles:', consolidatedData.roles);
          
          // Store the consolidated profile data
          setConsolidatedProfile(consolidatedData);
          
          // Map API response to local profile format for backward compatibility
          const mappedProfile: LocalUserProfile = {
            id: consolidatedData.user.id.toString(),
            firstName: consolidatedData.user.firstName || '',
            lastName: consolidatedData.user.lastName || '',
            email: consolidatedData.user.email || '',
            phone: consolidatedData.user.phoneNumber || '',
            role: consolidatedData.roles?.length > 0 ? (consolidatedData.roles[0].roleName || `Role ID: ${consolidatedData.roles[0].roleId}`) : 'No Role Assigned',
            department: consolidatedData.user.department || '',
            branch: consolidatedData.defaultBranchId?.toString() || '',
            employeeId: consolidatedData.user.employeeId || '',
            joinDate: consolidatedData.user.joinDate || '',
            profileImage: consolidatedData.user.profileImage || '',
            bio: consolidatedData.user.bio || '',
            preferences: {
              defaultBranch: consolidatedData.defaultBranchId?.toString() || '',
              emailNotifications: consolidatedData.preferences?.emailNotifications ?? false,
              pushNotifications: consolidatedData.preferences?.pushNotifications ?? false,
              darkMode: false, // Not in backend preferences
              language: consolidatedData.preferences?.language === 'en' ? 'English' : (consolidatedData.preferences?.language || 'English'),
              timezone: consolidatedData.preferences?.timezone || 'Asia/Kolkata'
            }
          };
          
          setProfile(mappedProfile);
          
          // Map recent activities to the local format
          if (consolidatedData.recentActivities?.length > 0) {
            const mappedActivities: ActivityLog[] = consolidatedData.recentActivities.map(activity => ({
              id: activity.id.toString(),
              action: activity.action,
              timestamp: new Date(activity.timestamp).toLocaleString(),
              device: activity.device || 'Unknown Device',
              location: activity.location || 'Unknown Location'
            }));
            setActivityLogs(mappedActivities);
          } else {
            // console.log('⚠️ No recent activities found or preferences is null, setting empty array');
            setActivityLogs([]);
          }
        }
      } catch (error) {
        // console.error('Failed to load consolidated user profile:', error);
        toast.error('Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    loadCurrentUserProfile();
  }, [currentUser?.id]); // Add currentUser.id as dependency

  const handleSaveProfile = async () => {
    // Check if we have the current user from context
    if (!currentUser?.id) {
      toast.error('User authentication required. Please login again.');
      return;
    }

    // Check if profile data is available
    if (!profile) {
      toast.error('Profile data not loaded. Please refresh and try again.');
      return;
    }

    try {
      setLoading(true);
      
      // Get the original user data from consolidated profile for comparison
      const originalUser = consolidatedProfile?.user;
      if (!originalUser) {
        toast.error('User data not loaded. Please refresh and try again.');
        return;
      }

      // Prepare the data to update - only include changed values
      const userUpdateData: Partial<User> = {};
      const preferencesUpdateData: Partial<UserPreferences> = {};
      
      // Check and add only changed user fields
      if (profile.firstName !== originalUser.firstName) {
        userUpdateData.firstName = profile.firstName;
      }
      if (profile.lastName !== originalUser.lastName) {
        userUpdateData.lastName = profile.lastName;
      }
      if (profile.email !== originalUser.email) {
        userUpdateData.email = profile.email;
      }
      if (profile.phone !== originalUser.phoneNumber) {
        userUpdateData.phoneNumber = profile.phone;
      }
      if (profile.department !== originalUser.department) {
        userUpdateData.department = profile.department;
      }
      if (profile.bio !== originalUser.bio) {
        userUpdateData.bio = profile.bio;
      }

      // Check and add only changed preference fields
      const originalPreferences = consolidatedProfile.preferences;
      
      if (profile.preferences.emailNotifications !== originalPreferences.emailNotifications) {
        preferencesUpdateData.emailNotifications = profile.preferences.emailNotifications;
      }
      if (profile.preferences.pushNotifications !== originalPreferences.pushNotifications) {
        preferencesUpdateData.pushNotifications = profile.preferences.pushNotifications;
      }
      if (profile.preferences.language !== (originalPreferences.language === 'en' ? 'English' : originalPreferences.language)) {
        preferencesUpdateData.language = profile.preferences.language === 'English' ? 'en' : originalPreferences.language;
      }
      if (profile.preferences.timezone !== originalPreferences.timezone) {
        preferencesUpdateData.timezone = profile.preferences.timezone;
      }

      // Format data according to the expected schema structure
      const completeUpdateData: any = {};
      
      // Only include user object if there are user changes
      if (Object.keys(userUpdateData).length > 0) {
        completeUpdateData.user = userUpdateData;
      }
      
      // Only include preferences object if there are preference changes
      if (Object.keys(preferencesUpdateData).length > 0) {
        completeUpdateData.preferences = preferencesUpdateData;
      }

      // Only proceed if there are actual changes
      if (Object.keys(completeUpdateData).length === 0) {
        toast.info('No changes detected');
        return;
      }

      // Debug: Log the final payload structure
      // console.log('🔍 Final Update Payload:', completeUpdateData);

      // Update complete user profile using the new dedicated endpoint
      const updatedBy = currentUser.id; // Use current user ID from context
      const profileResponse = await userApiService.updateCompleteUserProfile(
        currentUser.id, // Use current user ID from context
        completeUpdateData, 
        updatedBy
      );
      
      if (profileResponse.success) {
        toast.success('Profile updated successfully!');
      } else {
        toast.error(profileResponse.message || 'Failed to update profile');
      }
    } catch (error) {
      // console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Please enter your old password');
      return;
    }
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in new password and confirm password');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    if (!currentUser?.id) {
      toast.error('User authentication required. Please login again.');
      return;
    }
    try {
      await userApiService.changePassword(currentUser.id, currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      // Error toast handled in service
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getDeviceIcon = (device: string) => {
    if (device.toLowerCase().includes('iphone') || device.toLowerCase().includes('android')) {
      return <Smartphone className="h-4 w-4" />;
    }
    return <Monitor className="h-4 w-4" />;
  };

  // Check if user is authenticated
  if (!currentUser?.id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <UserIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Please login to view your profile</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading consolidated profile...</span>
      </div>
    );
  }

  // Check if profile data is loaded
  if (!profile || !consolidatedProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <UserIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Profile data not loaded</p>
          <p className="text-sm text-gray-400 mt-2">Please refresh the page or try again later</p>
        </div>
      </div>
    );
  }

  // Show warning if consolidated profile failed to load
  const showConsolidatedWarning = !loading && !consolidatedProfile;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
          User Profile
        </h1>
        <p className="text-gray-600">Manage your account settings and preferences</p>
      </div>

      {/* Warning if consolidated profile failed to load */}
      {showConsolidatedWarning && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800">
            <FileText className="h-5 w-5 text-primary-700" />
            <span className="font-medium">Limited Profile Information</span>
          </div>
          <p className="text-yellow-700 text-sm mt-1">
            Some profile information could not be loaded. Showing basic profile data only.
          </p>
        </div>
      )}

      {/* Profile Overview Card */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar className="h-24 w-24 border-4 border-primary-200">
                <AvatarFallback className="bg-gradient-to-br from-primary-500 to-primary-600 text-white text-xl">
                  {getInitials(profile.firstName, profile.lastName)}
                </AvatarFallback>
              </Avatar>
              <Button
                size="sm"
                className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-primary-500 hover:bg-primary-600 p-0"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex-1 space-y-2">
              <h2 className="text-2xl font-semibold text-primary-800">
                {profile.firstName} {profile.lastName}
              </h2>
              <div className="flex items-center gap-4 text-gray-600">
                <div className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  <span>{profile.role}</span>
                  {consolidatedProfile && consolidatedProfile.roles.length > 1 && (
                    <Badge variant="outline" className="ml-1 text-xs">
                      +{consolidatedProfile.roles.length - 1}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {consolidatedProfile ? 
                      (() => {
                        const defaultBranch = consolidatedProfile.branchAccess.find(b => b.branchId === consolidatedProfile.defaultBranchId);
                        return defaultBranch ? defaultBranch.branchName : `Branch ID: ${consolidatedProfile.defaultBranchId}`;
                      })() :
                      `Branch ID: ${profile.branch}`
                    }
                  </span>
                  {consolidatedProfile && consolidatedProfile.branchAccess.length > 1 && (
                    <Badge variant="outline" className="ml-1 text-xs">
                      +{consolidatedProfile.branchAccess.length - 1}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  <span>{profile.email}</span>
                </div>
              </div>
              <Badge className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
                Employee ID: {profile.employeeId}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consolidated Profile Information - removed sections per requirement */}

      {/* Tab Navigation */}
      <div className="flex border-b border-primary-200">
        {[
          { id: 'profile', label: 'Profile Information', icon: UserIcon },
          { id: 'security', label: 'Security', icon: Shield },
          // { id: 'preferences', label: 'Preferences', icon: Settings },
          // { id: 'activity', label: 'Activity Log', icon: Activity }
        ].map((tab) => (
          <Button
            key={tab.id}
            variant="ghost"
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-700 bg-primary-50'
                : 'border-transparent text-gray-600 hover:text-primary-600 hover:bg-primary-50'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Personal Information */}
          <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
                          <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Personal Information
            </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={profile.firstName}
                    onChange={(e) => handleProfileUpdate('firstName', e.target.value)}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={profile.lastName}
                    onChange={(e) => handleProfileUpdate('lastName', e.target.value)}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleProfileUpdate('email', e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={profile.phone}
                  onChange={(e) => handleProfileUpdate('phone', e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={profile.phone || ''}
                  onChange={(e) => handleProfileUpdate('phone', e.target.value)}
                  placeholder="Enter phone number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
            </CardContent>
          </Card>

          {/* Work Information */}
          <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
              <CardTitle className="flex items-center gap-2 text-primary-800">
                <FileText className="h-5 w-5 text-primary-700" />
                Work Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="employeeId">Employee ID</Label>
                <Input
                  id="employeeId"
                  value={profile.employeeId}
                  readOnly
                  className="bg-gray-50 border-gray-200"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <div className="space-y-2">
                  {consolidatedProfile ? (
                    <div className="flex flex-wrap gap-2">
                      {consolidatedProfile.roles.map((userRole) => (
                        <Badge 
                          key={userRole.id}
                          variant={userRole.isActive ? "default" : "secondary"}
                          className={`${
                            userRole.isActive 
                              ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white" 
                              : "bg-gray-400 text-white"
                          } px-3 py-1 text-sm`}
                        >
                          {userRole.roleName || `Role ID: ${userRole.roleId}`}
                          {userRole.isActive && (
                            <span className="ml-1 text-xs">●</span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <Input
                      id="role"
                      value={profile.role}
                      readOnly
                      className="bg-gray-50 border-gray-200"
                    />
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={profile.department}
                  onChange={(e) => handleProfileUpdate('department', e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="branch">Branch</Label>
                <div className="space-y-2">
                  {consolidatedProfile ? (
                    <div className="flex flex-wrap gap-2">
                      {consolidatedProfile.branchAccess.map((userBranch) => (
                        <Badge 
                          key={userBranch.id}
                          variant={userBranch.isActive ? "default" : "secondary"}
                          className={`${
                            userBranch.isActive 
                              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white" 
                              : "bg-gray-400 text-white"
                          } px-3 py-1 text-sm ${
                            userBranch.branchId === consolidatedProfile.defaultBranchId 
                              ? "ring-2 ring-yellow-400 ring-offset-1" 
                              : ""
                          }`}
                        >
                          {userBranch.branchName || `Branch ID: ${userBranch.branchId}`}
                          {userBranch.isActive && (
                            <span className="ml-1 text-xs">●</span>
                          )}
                          {userBranch.branchId === consolidatedProfile.defaultBranchId && (
                            <span className="ml-1 text-xs text-yellow-200">★</span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <Input
                      id="branch"
                      value={`Branch ID: ${profile.branch}`}
                      readOnly
                      className="bg-gray-50 border-gray-200"
                    />
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="joinDate">Join Date</Label>
                <Input
                  id="joinDate"
                  type="date"
                  value={profile.joinDate}
                  readOnly
                  className="bg-gray-50 border-gray-200"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'security' && (
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50 max-w-2xl">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Security Settings
            </CardTitle>
            <CardDescription>
              Update your password and security preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Old Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPasswords ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-0 top-0 h-full px-3 text-primary-500"
                  >
                    {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type={showPasswords ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPasswords ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
              
              <Button
                onClick={handleChangePassword}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
              >
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>
            </div>
            
            <Separator className="bg-primary-200" />
            
            <div className="space-y-4">
              <h3 className="font-medium text-primary-800">Two-Factor Authentication</h3>
              <p className="text-sm text-gray-600">
                Add an extra layer of security to your account
              </p>
              <Button variant="outline" className="border-primary-200 text-primary-700 hover:bg-primary-50">
                Enable 2FA
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preferences Tab - Commented Out */}
      {/* {activeTab === 'preferences' && (
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50 max-w-2xl">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Application Preferences
            </CardTitle>
            <CardDescription>
              Customize your application experience
              {consolidatedProfile && (
                <span className="text-xs text-gray-500 ml-2">
                  (Preferences loaded from backend - Language: {consolidatedProfile.preferences.language}, Timezone: {consolidatedProfile.preferences.timezone})
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select 
                  value={profile.preferences.language} 
                  onValueChange={(value) => handlePreferenceUpdate('language', value)}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="English">English</SelectItem>
                    <SelectItem value="Hindi">Hindi</SelectItem>
                    <SelectItem value="Marathi">Marathi</SelectItem>
                    <SelectItem value="Gujarati">Gujarati</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select 
                  value={profile.preferences.timezone} 
                  onValueChange={(value) => handlePreferenceUpdate('timezone', value)}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Asia/Kolkata">India Standard Time (IST)</SelectItem>
                    <SelectItem value="Asia/Dubai">Gulf Standard Time (GST)</SelectItem>
                    <SelectItem value="Europe/London">Greenwich Mean Time (GMT)</SelectItem>
                    <SelectItem value="America/New_York">Eastern Standard Time (EST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Separator className="bg-primary-200" />
            
            <div className="space-y-4">
              <h3 className="font-medium text-primary-800">Notifications</h3>
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Notifications
                  </Label>
                  <p className="text-sm text-gray-600">Receive notifications via email</p>
                </div>
                <Switch
                  checked={profile.preferences.emailNotifications}
                  onCheckedChange={(checked) => handlePreferenceUpdate('emailNotifications', checked)}
                  className="data-[state=checked]:bg-primary-500"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    Push Notifications
                  </Label>
                  <p className="text-sm text-gray-600">Receive push notifications in browser</p>
                </div>
                <Switch
                  checked={profile.preferences.pushNotifications}
                  onCheckedChange={(checked) => handlePreferenceUpdate('pushNotifications', checked)}
                  className="data-[state=checked]:bg-primary-500"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* Activity Log Tab - Commented Out */}
      {/* {activeTab === 'activity' && (
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Recent Activity
            </CardTitle>
            <CardDescription>
              Your recent login and system activity
              {consolidatedProfile && (
                <span className="text-xs text-gray-500 ml-2">
                  {consolidatedProfile.recentActivities.length > 0 ? 
                    `(Showing last ${consolidatedProfile.recentActivities.length} activities from backend)` :
                    '(No recent activities found in backend)'
                  }
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {activityLogs.length > 0 ? (
                activityLogs.map((log) => (
                  <div key={log.id} className="flex items-center gap-4 p-4 border border-primary-100 rounded-lg hover:bg-primary-50/50">
                    <div className="flex items-center gap-2 text-primary-600">
                      {getDeviceIcon(log.device)}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{log.action}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {log.timestamp}
                        </div>
                        <span>{log.device}</span>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {log.location}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p>No recent activity found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* Save Button - Only show for profile tab */}
      {activeTab === 'profile' && (
        <div className="flex justify-end">
          <Button
            onClick={handleSaveProfile}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}